var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { convertToNewFormStructure, getComponents, lookupComponent, hasValue, generateScriptPathFromModuleId, normalizeModuleScriptUrl, loadScriptFileAsync, getModuleIdFromWidget } from '@utils';
/**
 * Special widget IDs for dashboard placeholders and built-in widgets
 */
export const SPACER_WIDGET_ID = 'uxp-dashboard-spacer-widget';
export const PLACEHOLDER_WIDGET_ID = 'uxp-dashboard-placeholder-widget';
export const WIDGET_PANEL_ID = 'uxp-core/widget-panel';
export const DIAGNOSTIC_WIDGET_ID = 'uxp-core/diagnostic';
/**
 * Default breakpoints for responsive dashboard
 * NOTE: using default columns as 30 to prevent dashboards from breaking
 */
export const DEFAULT_BREAKPOINTS = {
    xxxl: { width: 2560, columns: 30, label: "4K / Ultra-wide", icon: "tv" },
    xxl: { width: 1920, columns: 30, label: "Full HD Desktop", icon: "desktop" },
    xl: { width: 1536, columns: 30, label: "Large Laptop / QHD", icon: "laptop" },
    lg: { width: 1280, columns: 30, label: "Laptop / Desktop", icon: "laptop-code" },
    md: { width: 1024, columns: 30, label: "Tablet Landscape", icon: "rectangle-wide" },
    sm: { width: 768, columns: 30, label: "Tablet Portrait", icon: "rectangle-vertical" },
    xs: { width: 480, columns: 30, label: "Mobile", icon: "mobile" },
    xxs: { width: 0, columns: 30, label: "Small Mobile", icon: "mobile-retro" },
};
export function processBreakpoints(customBreakpoints) {
    const bps = (!!customBreakpoints && Object.keys(customBreakpoints).length > 0)
        ? customBreakpoints
        : DEFAULT_BREAKPOINTS;
    // Sort breakpoints by width descending to find "next" breakpoint for width: 0 case
    const sortedEntries = Object.entries(bps).sort((a, b) => b[1].width - a[1].width);
    // Process breakpoint config for selector (handle width: 0 special case)
    const breakpointConfig = {};
    sortedEntries.forEach(([key, bp], index) => {
        if (bp.width === 0) {
            // Find next larger breakpoint (previous in sorted array since it's descending)
            const nextBreakpoint = index > 0 ? sortedEntries[index - 1][1] : null;
            const effectiveWidth = nextBreakpoint ? nextBreakpoint.width - 1 : 479;
            breakpointConfig[key] = Object.assign(Object.assign({}, bp), { width: effectiveWidth });
        }
        else {
            breakpointConfig[key] = bp;
        }
    });
    return Object.entries(bps).reduce((final, [key, { width, columns }]) => {
        final.breakpoints[key] = width;
        final.columns[key] = columns;
        return final;
    }, { breakpoints: {}, columns: {}, breakpointConfig });
}
// Calculate row height to maintain 1:1 ratio
export function calculateRowHeight(containerWidth, currentBreakpoint, columns, margin) {
    if (!containerWidth || !currentBreakpoint)
        return 30;
    const currentColumns = columns[currentBreakpoint] || 30;
    const horizontalMargin = margin[0];
    // Calculate cell width: (container width - total margins) / columns
    // Total margins = (columns - 1) * margin (spaces between cells)
    const totalHorizontalMargin = (currentColumns - 1) * horizontalMargin;
    const cellWidth = (containerWidth - totalHorizontalMargin) / currentColumns;
    // Return row height equal to cell width for 1:1 ratio
    return Math.floor(cellWidth);
}
export function processInitialWidgetsAndLayouts(initialWidgets, initialLayouts, breakpoints, columns, direction) {
    var _a, _b;
    // Compat: normalize widget IDs that changed between releases
    const LEGACY_ID_MAP = {
        'uxp-widget-panel': WIDGET_PANEL_ID,
        'uxp-core/widget/widget-panel': WIDGET_PANEL_ID, // 3-part → 2-part migration
    };
    // Compat: normalize layouts that used `static:true` (old lock mechanism) to isDraggable/isResizable
    const normalizeLayout = (layout) => {
        if (layout.static) {
            const _a = layout, { static: _s } = _a, rest = __rest(_a, ["static"]);
            return Object.assign(Object.assign({}, rest), { isDraggable: false, isResizable: false });
        }
        return layout;
    };
    const widgetPlaceholders = (initialWidgets || []).map(w => {
        const id = LEGACY_ID_MAP[w.id] ? LEGACY_ID_MAP[w.id] : w.id;
        return id !== w.id ? Object.assign(Object.assign({}, w), { id }) : w;
    });
    const rawLayouts = (Object.keys(initialLayouts).length === 0)
        // if layouts are empty(old configuration) extract from widgets
        ? convertOldLayoutToResponsive(widgetPlaceholders, Object.keys(breakpoints)).layouts
        : initialLayouts;
    // Compat: normalize static:true → isDraggable:false across all breakpoints
    const widgetPlaceholderLayouts = Object.fromEntries(Object.entries(rawLayouts).map(([bp, items]) => [bp, (items || []).map(normalizeLayout)]));
    // Align layouts to the current breakpoints:
    //   - Drop keys that aren't in breakpoints (avoids react-grid-layout prop-type warning)
    //   - Seed missing breakpoints from the best available fallback
    const validBreakpointKeys = Object.keys(breakpoints);
    const fallbackLayout = (_b = (_a = widgetPlaceholderLayouts['lg']) !== null && _a !== void 0 ? _a : Object.values(widgetPlaceholderLayouts).find(l => l && l.length > 0)) !== null && _b !== void 0 ? _b : [];
    const alignedLayouts = Object.fromEntries(validBreakpointKeys.map(bp => { var _a; return [bp, (_a = widgetPlaceholderLayouts[bp]) !== null && _a !== void 0 ? _a : fallbackLayout]; }));
    const cleand = removeDummyWidgets(widgetPlaceholders, alignedLayouts);
    return translateLayoutBasedOnDirection(cleand.widgets, cleand.layouts, direction, columns);
}
/**
 * check if widget layout is responsive
 */
export function isResponsiveLayoutConfiguration(widgetLayout) {
    // Presence of 'widgets' is the v5 signal — hybrid format { widgets, layouts, installed } is still v5
    return widgetLayout && widgetLayout.hasOwnProperty('widgets');
}
/**
 * Convert old dashboard layout to new responsive layout format
 */
export function convertOldLayoutToResponsive(oldWidgets, breakpoints) {
    const { widgets, baseLayouts } = oldWidgets.reduce((final, widget) => {
        const { layout } = widget;
        final.widgets.push(widget);
        final.baseLayouts.push(layout);
        return final;
    }, { widgets: [], baseLayouts: [] });
    const layouts = breakpoints.reduce((final, breakpoint) => {
        final[breakpoint] = baseLayouts; // using the same layout for all breakpoints. users can adjust later 
        return final;
    }, {});
    return {
        widgets: widgets,
        layouts: layouts
    };
}
export function translateLayoutBasedOnDirection(widgets, layouts, direction, columnsByBreakpoint) {
    var _a;
    // Check current direction from widgets
    const currentWidgetDirection = (_a = (widgets || []).find(w => hasValue(w === null || w === void 0 ? void 0 : w.direction))) === null || _a === void 0 ? void 0 : _a.direction;
    // If already in correct direction or no change needed, return as is
    if ((!currentWidgetDirection && direction === 'ltr') || currentWidgetDirection === direction) {
        return { widgets, layouts };
    }
    // Update widget directions
    const updatedWidgets = (widgets || []).map((w) => (Object.assign(Object.assign({}, w), { direction: direction })));
    // Translate all layouts for each breakpoint
    const updatedLayouts = {};
    Object.entries(layouts).forEach(([breakpoint, layout]) => {
        if (!layout)
            return;
        const columns = columnsByBreakpoint[breakpoint] || 30; // default to 30 if not found
        updatedLayouts[breakpoint] = layout.map((item) => (Object.assign(Object.assign({}, item), { x: columns - item.x - item.w })));
    });
    return {
        widgets: updatedWidgets,
        layouts: updatedLayouts
    };
}
/**
 * Sync a widget instance with its registered widget definition
 * Useful when widget code has been updated and needs to refresh
 *
 * @param widget - Widget instance to sync
 * @param currentWidgets - Current widgets array
 * @returns Updated widgets array
 */
export function syncWidget(widget, currentWidgets) {
    const registered = lookupComponent(widget.id);
    if (!registered) {
        console.warn('No registered widget found for sync:', widget.id);
        return currentWidgets;
    }
    return currentWidgets.map(w => {
        var _a;
        if (w._id === widget._id) {
            // Extract existing config values
            const existingValues = extractExistingConfigValues(widget);
            // Update configs with registered configs, preserving existing values
            const updatedConfigs = Object.assign(Object.assign({}, registered.configs), { props: convertToNewFormStructure(((_a = registered === null || registered === void 0 ? void 0 : registered.configs) === null || _a === void 0 ? void 0 : _a.props) || []).map(section => (Object.assign(Object.assign({}, section), { fields: section.fields.map(field => (Object.assign(Object.assign({}, field), { value: field.name in existingValues ? existingValues[field.name] : field.value }))) }))) });
            // Merge default props from widget and registered
            const mergedDefaultProps = Object.assign(Object.assign({}, widget === null || widget === void 0 ? void 0 : widget.defaultProps), registered === null || registered === void 0 ? void 0 : registered.defaultProps);
            const mergedConfiguredProps = Object.assign(Object.assign({}, mergedDefaultProps), (widget.configuredProps || {}));
            return Object.assign(Object.assign({}, w), { component: registered.component, configs: updatedConfigs, defaultProps: registered.defaultProps || w.defaultProps, configuredProps: mergedConfiguredProps });
        }
        return w;
    });
}
/**
 * Remove a widget from dashboard
 * Removes from widgets array and all responsive layouts
 *
 * @param widget - Widget instance to remove
 * @param currentWidgets - Current widgets array
 * @param currentLayouts - Current responsive layouts
 * @returns Object with updated widgets and layouts
 */
export function removeWidget(widget, currentWidgets, currentLayouts) {
    const updatedWidgets = currentWidgets.filter(w => w._id !== widget._id);
    // Remove from all layouts
    const updatedLayouts = {};
    Object.entries(currentLayouts).forEach(([breakpoint, layout]) => {
        if (layout) {
            updatedLayouts[breakpoint] = layout.filter(l => l.i !== widget._id);
        }
    });
    return {
        widgets: updatedWidgets,
        layouts: updatedLayouts
    };
}
/**
 * Update widget props
 *
 * @param instanceId - Widget instance ID
 * @param props - New props to merge
 * @param currentWidgets - Current widgets array
 * @returns Updated widgets array
 */
export function updateWidgetProps(instanceId, props, currentWidgets) {
    return currentWidgets.map(w => {
        if (w._id === instanceId) {
            return Object.assign(Object.assign({}, w), { configuredProps: mergeWidgetProps(w, props) });
        }
        return w;
    });
}
/**
 * Update widget configuration
 * Used when saving changes from config modal
 *
 * @param widget - Widget instance to update
 * @param configData - New configuration data
 * @param currentWidgets - Current widgets array
 * @returns Updated widgets array
 */
export function updateWidgetConfig(widget, configData, currentWidgets) {
    return currentWidgets.map(w => {
        var _a;
        if (w._id === widget._id) {
            // Update config props with new values
            const updatedConfigProps = ((_a = w.configs) === null || _a === void 0 ? void 0 : _a.props)
                ? updateConfigPropsWithValues(w.configs.props, configData)
                : [];
            return Object.assign(Object.assign({}, w), { configs: Object.assign(Object.assign({}, w.configs), { props: updatedConfigProps }), configuredProps: Object.assign(Object.assign({}, (w.configuredProps || {})), configData), hasConfigured: true });
        }
        return w;
    });
}
/**
 * Add new widgets to dashboard
 * Merges new widgets with existing ones and their layouts
 *
 * @param newWidgets - New widget instances to add
 * @param currentWidgets - Current widgets array
 * @param currentLayouts - Current responsive layouts
 * @returns Object with updated widgets and layouts
 */
export function addWidgets(newWidgets, currentWidgets, currentLayouts) {
    const updatedWidgets = [...currentWidgets, ...newWidgets];
    // Add new widget layouts to all breakpoints
    const updatedLayouts = Object.assign({}, currentLayouts);
    Object.keys(updatedLayouts).forEach(breakpoint => {
        const existingLayouts = updatedLayouts[breakpoint] || [];
        const newLayouts = newWidgets.map(w => (Object.assign(Object.assign({}, w.layout), { i: w._id, _id: w._id })));
        updatedLayouts[breakpoint] = [...existingLayouts, ...newLayouts];
    });
    return {
        widgets: updatedWidgets,
        layouts: updatedLayouts
    };
}
/**
 * Find widget instance by ID
 *
 * @param instanceId - Widget instance ID to find
 * @param widgets - Widgets array to search
 * @returns Widget instance or undefined
 */
export function findWidgetById(instanceId, widgets) {
    return widgets.find(w => w._id === instanceId);
}
/**
 * Check if widget has configuration options
 *
 * @param widget - Widget instance to check
 * @returns True if widget has configurable props
 */
export function hasConfigOptions(widget) {
    var _a, _b;
    return (((_a = widget.configs) === null || _a === void 0 ? void 0 : _a.props) != null && widget.configs.props.length > 0)
        || ((_b = widget.configs) === null || _b === void 0 ? void 0 : _b.configPanel) != null;
}
/**
 * Check if widget is draggable
 *
 * @param widget - Widget instance to check
 * @returns True if widget is draggable
 */
export function isDraggable(widget) {
    var _a, _b;
    return ((_b = (_a = widget.configs) === null || _a === void 0 ? void 0 : _a.layout) === null || _b === void 0 ? void 0 : _b.isDraggable) !== false;
}
/**
 * Get merged props from widget (defaultProps + configuredProps)
 * Used for rendering widgets and config modals
 *
 * @param widget - Widget instance
 * @returns Merged props object
 */
export function getMergedWidgetProps(widget) {
    let props = {};
    if (widget.hasOwnProperty("defaultProps")) {
        props = Object.assign({}, widget.defaultProps);
    }
    if (widget.configuredProps) {
        props = Object.assign(Object.assign({}, props), widget.configuredProps);
    }
    return props;
}
/**
 * Merge widget props with new values
 * Combines defaultProps, existing configuredProps, and new props in correct order
 *
 * @param widget - Widget instance
 * @param newProps - New props to merge
 * @returns Merged props object
 */
export function mergeWidgetProps(widget, newProps) {
    return Object.assign(Object.assign(Object.assign({}, widget.defaultProps), widget.configuredProps), newProps);
}
/**
 * Check if widget's defaultProps count matches config props count
 * Used to determine if widget can be auto-configured
 *
 * @param widget - Widget instance to check
 * @returns True if counts match
 */
export function doesDefaultPropsMatchConfigCount(widget) {
    var _a, _b;
    if (!widget.hasOwnProperty("defaultProps") || !widget.defaultProps) {
        return false;
    }
    const configPropsCount = ((_b = (_a = widget.configs) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.length) || 0;
    const defaultPropsCount = Object.keys(widget.defaultProps || {}).length;
    return configPropsCount > 0 && configPropsCount === defaultPropsCount;
}
/**
 * Extract existing config values from widget
 * Converts widget config props to a key-value object
 *
 * @param widget - Widget instance
 * @returns Object with config field names as keys and their values
 */
export function extractExistingConfigValues(widget) {
    var _a;
    return convertToNewFormStructure(((_a = widget === null || widget === void 0 ? void 0 : widget.configs) === null || _a === void 0 ? void 0 : _a.props) || [])
        .flatMap(section => section.fields)
        .reduce((acc, field) => {
        acc[field.name] = field.value;
        return acc;
    }, {});
}
/**
 * Update config props array with new values
 * Maps over config props and updates their value property
 *
 * @param configProps - Array of config prop definitions
 * @param values - Object with new values keyed by prop name
 * @returns Updated config props array
 */
export function updateConfigPropsWithValues(configProps, values) {
    return configProps.map((prop) => (Object.assign(Object.assign({}, prop), { value: values[prop.name] })));
}
/**
 * Check if widget should show configuration placeholder
 * Widget needs configuration if:
 * 1. It has not been configured yet (hasConfigured = false)
 * 2. It has configurable props
 * 3. Auto-configuration criteria not met
 *
 * @param widget - Widget instance to check
 * @returns True if widget needs configuration placeholder
 */
export function shouldShowPlaceholder(widget) {
    // If already configured, no placeholder needed
    if (widget.hasConfigured)
        return false;
    // If no config options, no placeholder needed
    const hasConfigOpts = hasConfigOptions(widget);
    if (!hasConfigOpts)
        return false;
    // Check if widget can be auto-configured
    // If defaultProps count matches config props count, widget is auto-configured
    if (doesDefaultPropsMatchConfigCount(widget)) {
        return false;
    }
    // Widget needs configuration
    return true;
}
/**
 * Check and auto-configure widget if possible
 * Auto-configures when widget has defaultProps matching all config props
 *
 * @param widget - Widget instance to check and update
 * @returns Updated widget instance with hasConfigured set if applicable
 */
export function checkAndAutoConfigureWidget(widget) {
    // Already configured, no need to check
    if (widget.hasConfigured)
        return widget;
    const hasConfigOpts = hasConfigOptions(widget);
    // No config options, mark as configured
    if (!hasConfigOpts) {
        return Object.assign(Object.assign({}, widget), { hasConfigured: true });
    }
    // Check if defaultProps match config props count, auto-configure if so
    if (doesDefaultPropsMatchConfigCount(widget)) {
        return Object.assign(Object.assign({}, widget), { hasConfigured: true });
    }
    return widget;
}
/**
 * Validate layouts to ensure all have required properties
 * Adds _id property if missing
 *
 * @param layouts - Responsive layouts to validate
 * @returns Validated layouts with all required properties
 */
export function validateLayouts(layouts) {
    const validated = {};
    Object.entries(layouts).forEach(([breakpoint, layout]) => {
        if (layout) {
            validated[breakpoint] = layout.map(l => (Object.assign(Object.assign({}, l), { _id: l.i // Ensure _id matches i
             })));
        }
    });
    return validated;
}
/**
 * Get layout for specific breakpoint with fallback
 *
 * @param layouts - Responsive layouts
 * @param breakpoint - Target breakpoint
 * @param fallbackBreakpoint - Fallback if target not found
 * @returns Layout array for breakpoint
 */
export function getLayoutForBreakpoint(layouts, breakpoint, fallbackBreakpoint = 'lg') {
    return layouts[breakpoint] || layouts[fallbackBreakpoint] || [];
}
/**
 * Update widget layouts for all breakpoints
 * Updates the layout property on widget instances to match the new layouts
 *
 * @param currentWidgets - Current widgets array
 * @param updatedLayouts - Updated responsive layouts
 * @param currentBreakpoint - Current active breakpoint
 * @returns Updated widgets array with new layout positions
 */
export function updateWidgetCurrentLayout(currentWidgets, updatedLayouts, currentBreakpoint) {
    return currentWidgets.map(widget => {
        var _a, _b;
        const layoutForWidget = (_a = updatedLayouts[currentBreakpoint]) === null || _a === void 0 ? void 0 : _a.find(l => l.i === widget._id);
        if (layoutForWidget) {
            return Object.assign(Object.assign({}, widget), { layout: {
                    i: layoutForWidget.i,
                    x: layoutForWidget.x,
                    y: layoutForWidget.y,
                    w: layoutForWidget.w,
                    h: layoutForWidget.h,
                    static: (_b = layoutForWidget.static) !== null && _b !== void 0 ? _b : false,
                    _id: widget._id,
                    minW: layoutForWidget.minW,
                    maxW: layoutForWidget.maxW,
                    minH: layoutForWidget.minH,
                    maxH: layoutForWidget.maxH,
                    moved: layoutForWidget.moved,
                    isDraggable: layoutForWidget.isDraggable,
                    isResizable: layoutForWidget.isResizable,
                    resizeHandles: layoutForWidget.resizeHandles,
                    isBounded: layoutForWidget.isBounded
                } });
        }
        return widget;
    });
}
/**
 * Refresh widget after addon installation
 * Mimics the old dashboard behavior from WidgetContainerBlock.tsx and dashboard_utils.ts
 *
 * @param widget - Widget instance to refresh
 * @param addOns - AddOns manager instance
 * @returns Updated widget instance with refreshed addon status
 */
export function refreshWidgetAfterAddonInstall(widget, addOns) {
    return __awaiter(this, void 0, void 0, function* () {
        // Wait for addon configuration to update
        yield new Promise(resolve => setTimeout(resolve, 500));
        // Refresh installed addons list
        yield addOns.refreshInstalledAddOns();
        // bundleId = first segment of widget.id — always the module/bundle identifier for script loading
        const bundleId = widget.id.split('/')[0];
        // moduleId via getModuleIdFromWidget may be usecaseId (UUID) — correct for addon service checks
        const moduleId = getModuleIdFromWidget(widget);
        // Resolve script URL: prefer the preserved sourceUrl (normalized to relative), fall back to
        // generating from bundleId. After installation, Lucy serves the bundle at the module endpoint.
        const rawUrl = widget.sourceUrl;
        const scriptUrl = rawUrl
            ? normalizeModuleScriptUrl(rawUrl)
            : generateScriptPathFromModuleId(bundleId);
        // Load widget script if not already registered
        let registeredInstance = lookupComponent(widget.id);
        if (!registeredInstance) {
            yield loadScriptFileAsync(scriptUrl, bundleId, true);
            registeredInstance = lookupComponent(widget.id);
        }
        // Update widget with registered component
        if (registeredInstance) {
            widget.component = registeredInstance.component;
        }
        // Switch sourceUrl to relative local module URL (replaces S3 URL or absolute account URL)
        widget.sourceUrl = generateScriptPathFromModuleId(bundleId);
        // Update addon installation status
        if (addOns.isAddOn(moduleId)) {
            const { installed } = addOns.isAddOnInstalledAndConfigured(moduleId);
            widget.installed = installed;
        }
        return widget;
    });
}
/**
 * Calculate spacer height based on breakpoint and columns
 * Larger heights for smaller screens to provide adequate scrolling space
 *
 * @param breakpoint - Current breakpoint name
 * @param columns - Number of columns for this breakpoint
 * @returns Height in grid units
 */
function calculateSpacerHeight(breakpoint, columns) {
    // Base height as a fraction of total columns
    // Smaller screens get a larger fraction for better UX
    const heightRatios = {
        'xxs': 0.35, // ~14 units for 40 columns (35%)
        'xs': 0.30, // ~12 units for 40 columns (30%)
        'sm': 0.20, // ~8 units for 40 columns (20%)
        'md': 0.15, // ~6 units for 40 columns (15%)
        'lg': 0.10 // ~4 units for 40 columns (10%)
    };
    const ratio = heightRatios[breakpoint] || heightRatios['lg'];
    const calculatedHeight = Math.ceil(columns * ratio);
    // Ensure minimum height of 3 and maximum of 15
    return Math.max(3, Math.min(15, calculatedHeight));
}
/**
 * Add spacer widget to create empty space at the bottom of dashboard
 */
export function addSpacerWidget(widgets, layouts, columns) {
    const cleaned = removeSpacerWidget(widgets, layouts);
    const layout = {
        x: 0,
        y: 0,
        w: 30,
        h: 3,
        i: SPACER_WIDGET_ID,
        static: true,
        isResizable: false,
        isDraggable: false
    };
    const spacerWidget = {
        _id: SPACER_WIDGET_ID,
        id: SPACER_WIDGET_ID,
        key: SPACER_WIDGET_ID,
        name: 'uxp-spacer-widget',
        component: null,
        layout: layout,
        hasConfigured: true,
        configs: {}
    };
    const updatedLayouts = {};
    Object.entries(cleaned.layouts).forEach(([breakpoint, breakpointLayouts]) => {
        const cols = columns[breakpoint] || 30;
        const spacerHeight = calculateSpacerHeight(breakpoint, cols);
        // Find the actual bottom position of the lowest widget in this breakpoint
        let maxBottom = 0;
        if (breakpointLayouts && breakpointLayouts.length > 0) {
            breakpointLayouts.forEach(layout => {
                const bottom = layout.y + layout.h;
                if (bottom > maxBottom)
                    maxBottom = bottom;
            });
        }
        // Position spacer just below the last widget
        updatedLayouts[breakpoint] = [
            ...(breakpointLayouts || []),
            Object.assign(Object.assign({}, layout), { y: maxBottom, w: cols, h: spacerHeight })
        ];
    });
    return {
        widgets: [...cleaned.widgets, spacerWidget],
        layouts: updatedLayouts
    };
}
/**
 * Remove spacer widget from widgets and layouts
 */
export function removeSpacerWidget(widgets, layouts) {
    const updatedWidgets = widgets.filter(w => w.id !== SPACER_WIDGET_ID);
    const updatedLayouts = {};
    Object.entries(layouts).forEach(([breakpoint, breakpointLayouts]) => {
        if (breakpointLayouts) {
            updatedLayouts[breakpoint] = breakpointLayouts.filter(l => l.i !== SPACER_WIDGET_ID);
        }
    });
    return {
        widgets: updatedWidgets,
        layouts: updatedLayouts
    };
}
/**
 * Add placeholder widget
 */
export function addPlaceholderWidget(widgets, layouts, columns, config) {
    var _a;
    const cleaned = removePlaceholderWidget(widgets, layouts);
    const BASE_COLS = 30;
    const BASE_W = 6;
    const BASE_H = 6;
    const MIN_SIZE = 4;
    // Scale base size from lg column count; clamp to MIN_SIZE
    const refCols = columns['lg'] || 30;
    const refScale = refCols / BASE_COLS;
    const scaledW = Math.max(MIN_SIZE, Math.round(BASE_W * refScale));
    const scaledH = Math.max(MIN_SIZE, Math.round(BASE_H * refScale));
    const layout = {
        x: 0,
        y: 0,
        w: scaledW,
        h: scaledH,
        i: PLACEHOLDER_WIDGET_ID,
        static: false,
        isResizable: false,
        isDraggable: false
    };
    // Adjust layout if message mode (full width)
    if ((_a = config === null || config === void 0 ? void 0 : config.message) === null || _a === void 0 ? void 0 : _a.enable) {
        layout.w = refCols;
        layout.h = Math.max(MIN_SIZE, Math.round(20 * refScale));
    }
    // Adjust for RTL direction
    if ((config === null || config === void 0 ? void 0 : config.direction) === 'rtl') {
        const cols = columns['lg'] || 30;
        layout.x = cols - layout.x - layout.w;
    }
    const placeholderWidget = {
        _id: PLACEHOLDER_WIDGET_ID,
        id: PLACEHOLDER_WIDGET_ID,
        key: PLACEHOLDER_WIDGET_ID,
        name: 'uxp-placeholder-widget',
        component: null,
        layout: layout,
        hasConfigured: true,
        configuredProps: Object.assign({}, config || {}),
        configs: {}
    };
    const updatedLayouts = {};
    Object.entries(cleaned.layouts).forEach(([breakpoint, breakpointLayouts]) => {
        var _a;
        const cols = columns[breakpoint] || 30;
        const bpScale = cols / BASE_COLS;
        const breakpointLayout = Object.assign({}, layout);
        // Scale width per breakpoint, capped to available columns
        if (!((_a = config === null || config === void 0 ? void 0 : config.message) === null || _a === void 0 ? void 0 : _a.enable)) {
            breakpointLayout.w = Math.min(cols, Math.max(MIN_SIZE, Math.round(BASE_W * bpScale)));
        }
        else {
            breakpointLayout.w = cols;
        }
        // Recalculate RTL position for this breakpoint
        if ((config === null || config === void 0 ? void 0 : config.direction) === 'rtl') {
            breakpointLayout.x = cols - layout.x - breakpointLayout.w;
        }
        breakpointLayout.i = PLACEHOLDER_WIDGET_ID;
        updatedLayouts[breakpoint] = [
            ...(breakpointLayouts || []),
            breakpointLayout
        ];
    });
    return {
        widgets: [placeholderWidget],
        layouts: updatedLayouts
    };
}
/**
 * Remove placeholder widget from widgets and layouts
 */
export function removePlaceholderWidget(widgets, layouts) {
    const updatedWidgets = widgets.filter(w => w.id !== PLACEHOLDER_WIDGET_ID);
    const updatedLayouts = {};
    Object.entries(layouts).forEach(([breakpoint, breakpointLayouts]) => {
        if (breakpointLayouts) {
            updatedLayouts[breakpoint] = breakpointLayouts.filter(l => l.i !== PLACEHOLDER_WIDGET_ID);
        }
    });
    return {
        widgets: updatedWidgets,
        layouts: updatedLayouts
    };
}
/**
 * Add both spacer and placeholder widgets as needed
 */
export function appendDummyWidgets(widgets, layouts, columns, config) {
    let result = removeDummyWidgets(widgets, layouts);
    result = addPlaceholderWidget(result.widgets, result.layouts, columns, config);
    result = addSpacerWidget(result.widgets, result.layouts, columns);
    return result;
}
/**
 * Remove all dummy widgets (spacer and placeholder)
 */
export function removeDummyWidgets(widgets, layouts) {
    let result = removeSpacerWidget(widgets, layouts);
    result = removePlaceholderWidget(result.widgets, result.layouts);
    return result;
}
/**
 * Get all registered background surfaces from the unified registry
 */
export function getBackgroundSurfaces() {
    return getComponents('background').map(c => {
        var _a, _b, _c;
        return ({
            type: c.id,
            label: (_a = c.name) !== null && _a !== void 0 ? _a : '',
            description: c.description,
            component: c.component,
            props: (_b = c.configs) === null || _b === void 0 ? void 0 : _b.props,
            configPanel: (_c = c.configs) === null || _c === void 0 ? void 0 : _c.configPanel,
            compatibleObjects: c.compatibleObjects,
        });
    });
}
/**
 * Get a specific background surface by type
 */
export function getBackgroundSurface(type) {
    return getBackgroundSurfaces().find(s => s.type === type);
}
